import { ConfigServiceModel } from '@app/origami-api/services/config.service';

import { Field } from '@app/origami-api/constants/field-flag';
import { forEach, mapValues, reduce, sortBy } from 'lodash';
import {
  Collection,
  LayoutField,
  LayoutGroup,
  LayoutSection,
} from '@app/pages/main/pages/quality/pages/quality-settings/pages/form-setting/components/form-layout-editor-modal/components/form-layout-editor/models';
import {
  isIgnoredField,
  resolveFieldTitle,
} from '@app/pages/main/pages/quality/pages/quality-settings/pages/form-setting/helpers';
import FormCollectionType = ConfigServiceModel.FormCollectionType;
import FormSectionAppearance = ConfigServiceModel.FormSectionAppearance;
import DEFAULT_SECTION_NAME = ConfigServiceModel.DEFAULT_SECTION_NAME;

export const parseFormLayout = (
  Collections: ConfigServiceModel.Form['Collections'],
  Fields: ConfigServiceModel.Field[],
  topicTypeId?: number,
) => {
  const {
    groups: rawGroups = [],
    sections: rawSections = [],
    parentMap,
  } = reduce(
    Collections,
    (
      acc,
      item,
    ): {
      sections: ConfigServiceModel.Section[];
      groups: ConfigServiceModel.Group[];
      parentMap: Record<string, string>;
    } => {
      if (item.Type === FormCollectionType.Group) {
        acc.groups.push(item);
      } else {
        acc.sections.push(item);
      }

      acc.parentMap[item.CollectionId] = item.Name;

      return acc;
    },
    { sections: [], groups: [], parentMap: {} },
  );

  const collectionMap: Record<string, (LayoutField | LayoutGroup)[]> = { [Collection.Excluded]: [] };
  const fieldsConfigMap: Record<string, ConfigServiceModel.Field> = {};
  const fieldMap: Record<string, LayoutField> = {};

  const sections: LayoutSection[] = sortBy(
    rawSections.map((section: ConfigServiceModel.Section) => ({
      id: section.CollectionId,
      title: section.Title,
      type: 'section',
      sortOrder: section.SortOrder,
      appearance: section.Appearance,
      default: section.IsDefault,
      name: section.Name,
    })),
    'sortOrder',
  );

  sections.forEach((section) => {
    collectionMap[section.name] = [];
  });

  if (sections.length === 0) {
    sections.push({
      id: -1,
      title: null,
      type: 'section',
      sortOrder: 0,
      appearance: FormSectionAppearance.Plain,
      default: true,
      name: DEFAULT_SECTION_NAME,
    });

    collectionMap[DEFAULT_SECTION_NAME] = [];
  }

  const defaultSection = sections.find((section) => section.default) || sections[0];

  // Collections arrive in an arbitrary order (the backend does not sort them), and a group may be
  // nested into another group, so every group needs its own bucket before parents are resolved.
  rawGroups.forEach((group: ConfigServiceModel.Group) => {
    collectionMap[group.Name] = collectionMap[group.Name] ?? [];
  });

  rawGroups.forEach((group: ConfigServiceModel.Group) => {
    const parentName = parentMap[group.ParentId] ?? defaultSection.name;

    collectionMap[parentName]?.push({
      type: 'group',
      id: group.CollectionId,
      parent: parentName,
      width: group.Width ?? 100,
      name: group.Name,
      title: group.Title,
      sortOrder: group.SortOrder,
    });
  });

  forEach(Fields, (field) => {
    if (isIgnoredField(field)) {
      return;
    }

    const resolvedTitle =
      topicTypeId != null ? resolveFieldTitle(field, topicTypeId) : field.Title || field.DefaultTitle;
    fieldsConfigMap[field.FieldName] = { ...field, Title: resolvedTitle };
    const parentName = parentMap[field.CollectionId];

    const item: LayoutField = {
      type: 'field',
      id: field.FieldName as unknown as number,
      width: field.Width ?? 100,
      sortOrder: field.SortOrder,
      name: field.FieldName,
      title: resolvedTitle,
      parent: collectionMap[parentName]
        ? parentName
        : Field.Is.includeInCoding(field)
          ? defaultSection.name
          : Collection.Excluded,
    };

    fieldMap[field.FieldName] = item;
    collectionMap[item.parent].push(item);
  });

  return {
    collectionMap: mapValues(collectionMap, (list) => sortBy(list, 'sortOrder')),
    fieldsConfigMap,
    sections,
    parentMap,
    fieldMap,
    rawSections,
    rawGroups,
  };
};
